import type { ImApi } from "./api";
import type { ConnectionStatus, PersistedMessage, SendDraft } from "./types";
import {
  HEARTBEAT_CLOSE_CODE,
  HEARTBEAT_CLOSE_REASON,
  HEARTBEAT_REQUEST,
  HEARTBEAT_RESPONSE,
  HeartbeatMonitor,
  heartbeatSettings,
} from "@sonox/realtime/heartbeat";

interface AckBody {
  readonly clientMsgId: string;
  readonly messageId: string;
  readonly convId: string;
  readonly seq: number;
  readonly serverTime: number;
}

/** JSON diagnostic protocol client with ACK tracking and bounded reconnection. */
export class ImSocket {
  private socket: WebSocket | null = null;
  private manuallyClosed = false;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private readonly heartbeat: HeartbeatMonitor;
  private readonly pending = new Map<string, {
    readonly resolve: (ack: AckBody) => void;
    readonly reject: (error: Error) => void;
    readonly timer: number;
  }>();

  public constructor(
    private readonly api: ImApi,
    private readonly onMessages: (messages: readonly PersistedMessage[]) => void,
    private readonly onStatus: (status: ConnectionStatus) => void,
    private readonly onError: (message: string) => void,
    private readonly onReconnect: () => void | Promise<void>,
  ) {
    this.heartbeat = new HeartbeatMonitor(
      heartbeatSettings(),
      () => this.sendHeartbeat(),
      () => { this.handleHeartbeatTimeout(); },
    );
  }

  public async connect(reconnecting = false): Promise<void> {
    this.manuallyClosed = false;
    this.onStatus(reconnecting ? "reconnecting" : "connecting");
    const ticket = await this.api.connectTicket();
    const url = new URL(ticket.webSocketUrl);
    url.searchParams.set("ticket", ticket.ticket);
    const socket = new WebSocket(url, "im.v3.json");
    this.socket = socket;
    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.reconnectAttempt = 0;
      this.onStatus("online");
      this.heartbeat.start();
      if (reconnecting) {
        void Promise.resolve(this.onReconnect()).catch((error: unknown) => {
          this.onError(error instanceof Error ? error.message : "重连后消息同步失败");
        });
      }
    });
    socket.addEventListener("message", (event) => { this.handleMessage(event.data); });
    socket.addEventListener("error", () => {
      this.onError("实时连接发生网络错误");
      this.handleDisconnected(socket, HEARTBEAT_CLOSE_CODE, "NETWORK_ERROR", true);
    });
    socket.addEventListener("close", (event) => {
      this.handleDisconnected(socket, event.code, event.reason, false);
    });
  }

  public async send(convId: string, draft: SendDraft): Promise<AckBody> {
    const socket = this.requireOpenSocket();
    const requestId = crypto.randomUUID();
    const clientMsgId = draft.clientMsgId ?? crypto.randomUUID();
    const frame = {
      type: "SEND",
      version: 3,
      requestId,
      timestamp: Date.now(),
      body: {
        clientMsgId,
        convId,
        contentType: draft.contentType,
        payload: draft.payload,
        ...(draft.replyTo === undefined ? {} : { replyTo: draft.replyTo }),
        ...(draft.targetMessageId === undefined
          ? {}
          : { targetMessageId: draft.targetMessageId }),
        ...(draft.mentionedUserIds === undefined
          ? {}
          : { mentionedUserIds: draft.mentionedUserIds }),
      },
    };
    const result = new Promise<AckBody>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error("消息确认超时，请同步记录后重试"));
      }, 15_000);
      this.pending.set(requestId, { resolve, reject, timer });
    });
    socket.send(JSON.stringify(frame));
    return result;
  }

  public markRead(convId: string, seq: number): void {
    const socket = this.socket;
    if (socket?.readyState !== WebSocket.OPEN || seq < 1) return;
    socket.send(JSON.stringify({
      type: "CURSOR",
      version: 3,
      requestId: crypto.randomUUID(),
      timestamp: Date.now(),
      body: { convId, deliveredSeq: seq, readSeq: seq },
    }));
  }

  public close(): void {
    this.manuallyClosed = true;
    this.clearReconnectTimer();
    this.heartbeat.stop();
    const socket = this.socket;
    this.socket = null;
    socket?.close(1000, "client logout");
    this.rejectPending("实时连接已关闭");
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== "string") return;
    if (data === HEARTBEAT_RESPONSE) {
      this.heartbeat.acknowledge();
      return;
    }
    let frame: { type?: string; requestId?: string; body?: unknown };
    try {
      frame = JSON.parse(data) as typeof frame;
    } catch {
      this.onError("收到无法解析的实时消息");
      return;
    }
    if (frame.type === "PONG") {
      this.heartbeat.acknowledge();
      return;
    }
    if (frame.type === "SERVER_ACK" && typeof frame.requestId === "string") {
      const pending = this.pending.get(frame.requestId);
      if (pending !== undefined) {
        window.clearTimeout(pending.timer);
        this.pending.delete(frame.requestId);
        pending.resolve(frame.body as AckBody);
      }
      return;
    }
    if (frame.type === "MESSAGES") {
      const messages = (frame.body as { messages?: PersistedMessage[] } | undefined)?.messages;
      if (Array.isArray(messages)) this.onMessages(messages);
      return;
    }
    if (frame.type === "ERROR") {
      const body = frame.body as { message?: string } | undefined;
      this.onError(body?.message ?? "实时服务拒绝了请求");
      return;
    }
    if (frame.type === "RECONNECT_REQUIRED" || frame.type === "SYNC_REQUIRED") {
      const socket = this.socket;
      if (socket !== null) this.handleDisconnected(socket, 4001, frame.type, true);
    }
  }

  private requireOpenSocket(): WebSocket {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      throw new Error("实时连接尚未就绪");
    }
    return this.socket;
  }

  private scheduleReconnect(): void {
    if (this.manuallyClosed || this.reconnectTimer !== null) return;
    this.onStatus("reconnecting");
    const delay = reconnectDelay(this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.manuallyClosed) {
        void this.connect(true).catch((error: unknown) => {
          this.onError(error instanceof Error ? error.message : "重连失败");
          this.scheduleReconnect();
        });
      }
    }, delay);
  }

  /** Uses ConnDO's raw auto-response so a heartbeat does not wake a hibernating shard. */
  private sendHeartbeat(): boolean {
    const socket = this.socket;
    if (socket?.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(HEARTBEAT_REQUEST);
      return true;
    } catch {
      return false;
    }
  }

  private handleHeartbeatTimeout(): void {
    const socket = this.socket;
    if (socket === null) return;
    this.onError("实时连接心跳超时，正在重新连接");
    this.handleDisconnected(
      socket,
      HEARTBEAT_CLOSE_CODE,
      HEARTBEAT_CLOSE_REASON,
      true,
    );
  }

  private handleDisconnected(
    socket: WebSocket,
    code: number,
    reason: string,
    closeSocket: boolean,
  ): void {
    if (this.socket !== socket) return;
    this.socket = null;
    this.heartbeat.stop();
    this.rejectPending("实时连接已断开，请同步记录后重试");
    if (closeSocket && socket.readyState < WebSocket.CLOSING) socket.close(code, reason);
    this.onStatus("offline");
    if (!this.manuallyClosed) this.scheduleReconnect();
  }

  private rejectPending(message: string): void {
    for (const pending of this.pending.values()) {
      window.clearTimeout(pending.timer);
      pending.reject(new Error(message));
    }
    this.pending.clear();
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
}

/** Adds bounded jitter so regional recovery does not create a reconnect wave. */
function reconnectDelay(attempt: number): number {
  const exponential = Math.min(15_000, 750 * 2 ** Math.min(attempt, 5));
  const sample = new Uint32Array(1);
  crypto.getRandomValues(sample);
  const ratio = (sample[0] ?? 0) / 0xffff_ffff;
  return Math.min(15_000, Math.round(exponential * (0.8 + ratio * 0.4)));
}
